import { Product, ProductVariant, getDisplayCategory } from '../types/product';

/**
 * Extract base ID and variant suffix from a product ID
 * Pattern: "A0256 G" → base = "A0256", variant = "G"
 * Pattern: "A0503-13 AG" → base = "A0503-13", variant = "AG"
 */
export function extractVariantInfo(productId: string): { baseId: string; variantSuffix: string | null } {
  const match = productId.match(/^(.+?)\s+([A-Z]+)$/);

  if (match) {
    return {
      baseId: match[1],
      variantSuffix: match[2],
    };
  }

  return {
    baseId: productId,
    variantSuffix: null,
  };
}

/**
 * Get priority for sorting variants
 * Gold (G, AG) = 0, Silver (S, AS) = 1, Bronze (B, AB) = 2, Others by char code
 */
export function getVariantPriority(suffix: string | null): number {
  if (!suffix) return 999; // No suffix = lowest priority
  if (suffix === 'G' || suffix === 'AG') return 0; // Gold first
  if (suffix === 'S' || suffix === 'AS') return 1; // Silver second
  if (suffix === 'B' || suffix === 'AB') return 2; // Bronze third
  return suffix.charCodeAt(0); // Others by character code
}

/**
 * Get display name for variant suffix
 */
export function getVariantDisplayName(suffix: string | null): string {
  if (!suffix) return '';
  if (suffix === 'G' || suffix === 'AG') return 'Gold';
  if (suffix === 'S' || suffix === 'AS') return 'Silver';
  if (suffix === 'B' || suffix === 'AB') return 'Bronze';
  return suffix;
}

/**
 * Group products by their base ID and create ProductVariant objects
 * Filters out ribbons from variant grouping
 */
export function groupProductsIntoVariants(products: Product[]): ProductVariant[] {
  // Filter out ribbons from variant grouping
  const nonRibbonProducts = products.filter(
    (p) => getDisplayCategory(p).toLowerCase() !== 'ribbon'
  );

  // Group products by base ID
  const groupedProducts = new Map<string, Product[]>();

  nonRibbonProducts.forEach((product) => {
    const { baseId } = extractVariantInfo(product.productId);

    if (!groupedProducts.has(baseId)) {
      groupedProducts.set(baseId, []);
    }
    groupedProducts.get(baseId)!.push(product);
  });

  // Create ProductVariant objects
  const variants: ProductVariant[] = [];

  groupedProducts.forEach((productGroup, baseId) => {
    // Sort variants by priority (Gold > Silver > Bronze)
    const sortedVariants = productGroup.sort((a, b) => {
      const { variantSuffix: suffixA } = extractVariantInfo(a.productId);
      const { variantSuffix: suffixB } = extractVariantInfo(b.productId);
      return getVariantPriority(suffixA) - getVariantPriority(suffixB);
    });

    const defaultVariant = sortedVariants[0];
    const prices = sortedVariants.map((p) => p.price || 0).filter((p) => p > 0);
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

    // Format price display
    let priceDisplay: string;
    if (prices.length === 0) {
      priceDisplay = 'N/A';
    } else if (minPrice === maxPrice) {
      priceDisplay = `€${minPrice.toFixed(2)}`;
    } else {
      priceDisplay = `€${minPrice.toFixed(2)} - €${maxPrice.toFixed(2)}`;
    }

    variants.push({
      name: baseId,
      description: defaultVariant.description,
      category: defaultVariant.category,
      variants: sortedVariants,
      defaultVariant,
      minPrice,
      maxPrice,
      priceDisplay,
      hasMultipleVariants: sortedVariants.length > 1,
      baseId,
    });
  });

  return variants;
}

/**
 * Sort ProductVariants by page number and then by base ID
 */
export function sortVariants(variants: ProductVariant[]): ProductVariant[] {
  return variants.sort((a, b) => {
    // Priced items first, non-priced items last
    const aHasPrice = a.minPrice > 0 ? 0 : 1;
    const bHasPrice = b.minPrice > 0 ? 0 : 1;
    if (aHasPrice !== bHasPrice) {
      return aHasPrice - bHasPrice;
    }

    const pageA = a.defaultVariant.page;
    const pageB = b.defaultVariant.page;

    if (pageA !== pageB) {
      return pageA - pageB;
    }

    return a.baseId.localeCompare(b.baseId);
  });
}
